import React, { useState } from 'react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

const S = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  topbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 28px', borderBottom: '0.5px solid var(--border)',
    position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10,
  },
  topLeft: { display: 'flex', alignItems: 'center', gap: '24px' },
  logo: {
    fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 300,
    fontStyle: 'italic', color: 'var(--accent)',
  },
  navBtn: {
    fontSize: '12px', color: 'var(--text-secondary)', background: 'none',
    border: 'none', cursor: 'pointer', padding: '4px 10px',
    borderRadius: 'var(--radius-sm)', transition: 'all 0.15s', letterSpacing: '0.04em',
  },
  navBtnActive: {
    fontSize: '12px', color: 'var(--accent)', background: 'var(--accent-dim)',
    border: 'none', cursor: 'pointer', padding: '4px 10px',
    borderRadius: 'var(--radius-sm)', letterSpacing: '0.04em',
  },
  userRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: 26, height: 26, borderRadius: '50%', border: '0.5px solid var(--border)' },
  userName: { fontSize: '12px', color: 'var(--text-secondary)' },
  logoutBtn: {
    fontSize: '11px', color: 'var(--text-tertiary)', background: 'none',
    border: 'none', cursor: 'pointer', padding: '4px 8px',
  },
  main: { padding: '24px 28px', maxWidth: '860px', margin: '0 auto' },
  heading: {
    fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 300,
    fontStyle: 'italic', color: 'var(--text-primary)', marginBottom: '2px',
  },
  sub: { fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '24px', letterSpacing: '0.05em' },
  controls: { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
  searchInput: {
    flex: 1, minWidth: 200, height: 32, padding: '0 12px', fontSize: '12px',
    border: '0.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
    background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none',
    transition: 'border-color 0.15s',
  },
  filterBtn: (active) => ({
    fontSize: '11px', padding: '4px 12px', borderRadius: '20px',
    border: `0.5px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    background: active ? 'var(--accent-dim)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
    cursor: 'pointer', transition: 'all 0.15s', letterSpacing: '0.03em',
  }),
  sortBtn: (active) => ({
    fontSize: '11px', padding: '4px 10px', borderRadius: 'var(--radius-sm)',
    border: '0.5px solid var(--border)', background: active ? 'var(--bg-hover)' : 'transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
    cursor: 'pointer', transition: 'all 0.15s',
  }),
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontSize: '10px', color: 'var(--text-tertiary)', letterSpacing: '0.1em',
    textTransform: 'uppercase', padding: '6px 10px', textAlign: 'left',
    borderBottom: '0.5px solid var(--border)', fontWeight: 400,
  },
  tr: { borderBottom: '0.5px solid var(--border)', transition: 'background 0.1s' },
  td: { padding: '10px 10px', fontSize: '13px', color: 'var(--text-primary)', verticalAlign: 'middle' },
  tdSub: { fontSize: '11px', color: 'var(--text-tertiary)', marginTop: 2 },
  badge: (type) => {
    const map = {
      task: { background: 'var(--bg-hover)', color: 'var(--text-secondary)' },
      decision: { background: 'var(--blue-dim)', color: 'var(--blue)' },
      'follow-up': { background: 'var(--accent-dim)', color: 'var(--accent)' },
      action: { background: 'var(--bg-hover)', color: 'var(--text-secondary)' },
      milestone: { background: 'var(--blue-dim)', color: 'var(--blue)' },
      high: { background: 'var(--red-dim)', color: 'var(--red)' },
      med: { background: 'var(--amber-dim)', color: 'var(--amber)' },
      low: { background: 'var(--green-dim)', color: 'var(--green)' },
    };
    const style = map[type] || map.task;
    return { ...style, fontSize: '10px', padding: '2px 7px', borderRadius: '10px', display: 'inline-block' };
  },
  empty: { fontSize: '13px', color: 'var(--text-tertiary)', textAlign: 'center', padding: '48px 0' },
  count: { fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: 'auto' },
};

export default function ArchivePage({ tasks, decisions, followups, onBack, user, onLogout }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const allItems = [
    ...tasks.filter(t => t.done).map(t => ({
      id: `t-${t.id}`, text: t.text, type: 'task', subtype: t.category,
      priority: t.priority, doneAt: t.doneAt, detail: t.priority,
    })),
    ...decisions.filter(d => d.done).map(d => ({
      id: `d-${d.id}`, text: d.title, type: 'decision', subtype: null,
      priority: null, doneAt: d.doneAt, detail: d.dueDate ? `due ${formatDate(d.dueDate)}` : null,
    })),
    ...followups.filter(f => f.done).map(f => ({
      id: `f-${f.id}`, text: f.name, type: 'follow-up', subtype: f.project || null,
      priority: null, doneAt: f.doneAt, detail: f.note || null,
    })),
  ];

  const filtered = allItems
    .filter(item => typeFilter === 'all' || item.type === typeFilter)
    .filter(item => {
      if (!search) return true;
      const q = search.toLowerCase();
      return item.text.toLowerCase().includes(q) ||
        (item.subtype && item.subtype.toLowerCase().includes(q)) ||
        (item.detail && item.detail.toLowerCase().includes(q));
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.doneAt || 0) - new Date(a.doneAt || 0);
      if (sortBy === 'type') return a.type.localeCompare(b.type);
      return a.text.localeCompare(b.text);
    });

  const counts = {
    all: allItems.length,
    task: allItems.filter(i => i.type === 'task').length,
    decision: allItems.filter(i => i.type === 'decision').length,
    'follow-up': allItems.filter(i => i.type === 'follow-up').length,
  };

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div style={S.topLeft}>
          <div style={S.logo}>decodification</div>
          <button style={S.navBtn} onClick={onBack}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'none'; }}>
            today
          </button>
          <button style={S.navBtnActive}>archive</button>
        </div>
        <div style={S.userRow}>
          {user.picture && <img src={user.picture} alt="" style={S.avatar} referrerPolicy="no-referrer" />}
          <span style={S.userName}>{user.name}</span>
          <button style={S.logoutBtn} onClick={onLogout}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}>sign out</button>
        </div>
      </div>

      <div style={S.main}>
        <div style={S.heading}>archive</div>
        <div style={S.sub}>everything you've completed · {allItems.length} items total</div>

        <div style={S.controls}>
          <input style={S.searchInput} placeholder="Search..."
            value={search} onChange={e => setSearch(e.target.value)}
            onFocus={e => e.target.style.borderColor = 'var(--border-hover)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          {['all','task','decision','follow-up'].map(t => (
            <button key={t} style={S.filterBtn(typeFilter === t)} onClick={() => setTypeFilter(t)}>
              {t === 'all' ? `all (${counts.all})` : `${t}s (${counts[t]})`}
            </button>
          ))}
          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', alignSelf: 'center', marginRight: 4 }}>sort:</span>
            {['date','type','name'].map(s => (
              <button key={s} style={S.sortBtn(sortBy === s)} onClick={() => setSortBy(s)}>{s}</button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div style={S.empty}>{search ? 'no results found' : 'nothing archived yet'}</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>item</th>
                <th style={S.th}>type</th>
                <th style={S.th}>detail</th>
                <th style={S.th}>completed</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.id} style={S.tr}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>
                    <div>{item.text}</div>
                    {item.detail && item.type === 'follow-up' && (
                      <div style={S.tdSub}>{item.detail}</div>
                    )}
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(item.type)}>{item.type}</span>
                    {item.subtype && <span style={{ ...S.badge(item.subtype), marginLeft: 4 }}>{item.subtype}</span>}
                  </td>
                  <td style={S.td}>
                    {item.priority && <span style={S.badge(item.priority)}>{item.priority}</span>}
                    {!item.priority && item.detail && item.type !== 'follow-up' && (
                      <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{item.detail}</span>
                    )}
                  </td>
                  <td style={{ ...S.td, color: 'var(--text-tertiary)', fontSize: '12px' }}>
                    {formatDate(item.doneAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
